import type { Stats, GamificationData } from "@/types";
import type { StorageManager } from "@/utils/storage";
import { eventManager } from "@/utils/event-manager";

/**
 * Form stats interface
 */
interface FormStats {
  [tense: string]: {
    [pronoun: string]: {
      total: number;
      accuracy: number;
    };
  };
}

/**
 * Session stats interface
 */
interface SessionStats {
  todayTotal?: number;
}

/**
 * Achievement definition
 */
interface AchievementDefinition {
  name: string;
  description: string;
  icon: string;
  checkCondition: (
    stats: Stats,
    gamification: GamificationData,
    formStats?: FormStats,
    sessionStats?: SessionStats
  ) => boolean;
}

/**
 * Achievement with status
 */
interface AchievementWithStatus extends AchievementDefinition {
  id: string;
  earned: boolean;
  earnedDate: Date | null;
}

/**
 * Achievement progress
 */
interface AchievementProgress extends AchievementDefinition {
  id: string;
  progress: number;
}

/**
 * Achievement system
 */
export class AchievementSystem {
  private storage: StorageManager;
  private achievements: string[];
  private definitions: Record<string, AchievementDefinition>;

  constructor(storageManager: StorageManager) {
    this.storage = storageManager;
    this.achievements = this.loadAchievements();
    this.definitions = this.getAchievementDefinitions();
  }

  /**
   * Load achievements from storage
   */
  private loadAchievements(): string[] {
    return this.storage.load("achievements", []) || [];
  }

  /**
   * Get achievement definitions
   */
  private getAchievementDefinitions(): Record<string, AchievementDefinition> {
    return {
      "first-steps": {
        name: "First Steps",
        description: "Complete your first 10 exercises",
        icon: "👶",
        checkCondition: (stats) => stats.total >= 10,
      },
      "getting-started": {
        name: "Getting Started",
        description: "Complete 25 exercises",
        icon: "🚀",
        checkCondition: (stats) => stats.total >= 25,
      },
      "half-century": {
        name: "Half Century",
        description: "Complete 50 exercises",
        icon: "🎯",
        checkCondition: (stats) => stats.total >= 50,
      },
      century: {
        name: "Century Club",
        description: "Complete 100 exercises",
        icon: "💯",
        checkCondition: (stats) => stats.total >= 100,
      },
      dedicated: {
        name: "Dedicated Learner",
        description: "Complete 250 exercises",
        icon: "📚",
        checkCondition: (stats) => stats.total >= 250,
      },
      master: {
        name: "Master Learner",
        description: "Complete 500 exercises",
        icon: "🏆",
        checkCondition: (stats) => stats.total >= 500,
      },
      "streak-starter": {
        name: "Streak Starter",
        description: "Get 5 correct answers in a row",
        icon: "🔥",
        checkCondition: (_stats, gamification) =>
          gamification.streaks.current >= 5,
      },
      "streak-master": {
        name: "Streak Master",
        description: "Get 10 correct answers in a row",
        icon: "🌟",
        checkCondition: (_stats, gamification) =>
          gamification.streaks.current >= 10,
      },
      "streak-legend": {
        name: "Streak Legend",
        description: "Get 20 correct answers in a row",
        icon: "⭐",
        checkCondition: (_stats, gamification) =>
          gamification.streaks.current >= 20,
      },
      perfectionist: {
        name: "Perfectionist",
        description: "Achieve 90% accuracy with 20+ answers",
        icon: "💎",
        checkCondition: (stats) => stats.total >= 20 && stats.accuracy >= 90,
      },
      "accuracy-expert": {
        name: "Accuracy Expert",
        description: "Achieve 95% accuracy with 50+ answers",
        icon: "🎖️",
        checkCondition: (stats) => stats.total >= 50 && stats.accuracy >= 95,
      },
      "daily-warrior": {
        name: "Daily Warrior",
        description: "Practice for 7 consecutive days",
        icon: "🗡️",
        checkCondition: (_stats, gamification) =>
          gamification.streaks.daily >= 7,
      },
      "weekly-champion": {
        name: "Weekly Champion",
        description: "Practice for 14 consecutive days",
        icon: "👑",
        checkCondition: (_stats, gamification) =>
          gamification.streaks.daily >= 14,
      },
      "monthly-master": {
        name: "Monthly Master",
        description: "Practice for 30 consecutive days",
        icon: "🏰",
        checkCondition: (_stats, gamification) =>
          gamification.streaks.daily >= 30,
      },
      "level-up": {
        name: "Level Up",
        description: "Reach level 5",
        icon: "📈",
        checkCondition: (_stats, gamification) => gamification.level >= 5,
      },
      "high-achiever": {
        name: "High Achiever",
        description: "Reach level 10",
        icon: "🚀",
        checkCondition: (_stats, gamification) => gamification.level >= 10,
      },
      "elite-learner": {
        name: "Elite Learner",
        description: "Reach level 15",
        icon: "💫",
        checkCondition: (_stats, gamification) => gamification.level >= 15,
      },
      "verb-explorer": {
        name: "Verb Explorer",
        description: "Practice all verb forms at least once",
        icon: "🧭",
        checkCondition: (_stats, _gamification, formStats) => {
          const verbData = (window as any).verbData;
          if (!verbData || !formStats) return false;

          const allForms = verbData.getAllForms("all");
          return allForms.every((form: any) => {
            const stat = formStats[form.tense]?.[form.pronoun];
            return stat && stat.total > 0;
          });
        },
      },
      "form-master": {
        name: "Form Master",
        description: "Master 10 verb forms (80%+ accuracy)",
        icon: "🎭",
        checkCondition: (_stats, _gamification, formStats) => {
          if (!formStats) return false;

          let masteredCount = 0;
          for (const tense of Object.values(formStats)) {
            for (const stat of Object.values(tense)) {
              if (stat.total >= 5 && stat.accuracy >= 80) {
                masteredCount++;
              }
            }
          }
          return masteredCount >= 10;
        },
      },
      "speed-learner": {
        name: "Speed Learner",
        description: "Complete 20 exercises in one day",
        icon: "⚡",
        checkCondition: (_stats, _gamification, _formStats, sessionStats) => {
          return sessionStats?.todayTotal
            ? sessionStats.todayTotal >= 20
            : false;
        },
      },
    };
  }

  /**
   * Check for new achievements
   */
  checkAchievements(
    stats: Stats,
    gamification: GamificationData,
    formStats: FormStats = {},
    sessionStats: SessionStats = {}
  ): string[] {
    const newAchievements: string[] = [];

    for (const [id, definition] of Object.entries(this.definitions)) {
      // Skip if already earned
      if (this.achievements.includes(id)) continue;

      // Check condition
      if (
        definition.checkCondition(stats, gamification, formStats, sessionStats)
      ) {
        newAchievements.push(id);
        this.achievements.push(id);
      }
    }

    if (newAchievements.length > 0) {
      this.saveAchievements();

      // Emit achievement event
      eventManager.emit("achievement-unlocked", newAchievements);
    }

    return newAchievements;
  }

  /**
   * Get achievement by ID
   */
  getAchievement(id: string): AchievementWithStatus | null {
    const definition = this.definitions[id];
    if (!definition) return null;

    return {
      id,
      ...definition,
      earned: this.achievements.includes(id),
      earnedDate: this.achievements.includes(id) ? new Date() : null, // TODO: track actual dates
    };
  }

  /**
   * Get all achievements with status
   */
  getAllAchievements(): AchievementWithStatus[] {
    return Object.keys(this.definitions)
      .map((id) => this.getAchievement(id))
      .filter(Boolean) as AchievementWithStatus[];
  }

  /**
   * Get earned achievements
   */
  getEarnedAchievements(): AchievementWithStatus[] {
    return this.achievements
      .map((id) => this.getAchievement(id))
      .filter(Boolean) as AchievementWithStatus[];
  }

  /**
   * Get progress towards unearned achievements
   */
  getAchievementProgress(
    stats: Stats,
    gamification: GamificationData,
    _formStats: FormStats = {}
  ): AchievementProgress[] {
    const progress: AchievementProgress[] = [];

    for (const [id, definition] of Object.entries(this.definitions)) {
      if (this.achievements.includes(id)) continue;

      // Calculate progress percentage (this is simplified - could be more sophisticated)
      let progressPercent = 0;

      // Basic progress calculation based on common patterns
      if (definition.description.includes("exercises")) {
        const match = definition.description.match(/(\d+) exercises/);
        if (match) {
          const target = parseInt(match[1]);
          progressPercent = Math.min(
            100,
            Math.round((stats.total / target) * 100)
          );
        }
      } else if (definition.description.includes("in a row")) {
        const match = definition.description.match(
          /(\d+) correct answers in a row/
        );
        if (match) {
          const target = parseInt(match[1]);
          progressPercent = Math.min(
            100,
            Math.round((gamification.streaks.current / target) * 100)
          );
        }
      } else if (definition.description.includes("level")) {
        const match = definition.description.match(/level (\d+)/);
        if (match) {
          const target = parseInt(match[1]);
          progressPercent = Math.min(
            100,
            Math.round((gamification.level / target) * 100)
          );
        }
      } else if (definition.description.includes("consecutive days")) {
        const match = definition.description.match(/(\d+) consecutive days/);
        if (match) {
          const target = parseInt(match[1]);
          progressPercent = Math.min(
            100,
            Math.round((gamification.streaks.daily / target) * 100)
          );
        }
      }

      if (progressPercent > 0) {
        progress.push({
          id,
          ...definition,
          progress: progressPercent,
        });
      }
    }

    return progress.sort((a, b) => b.progress - a.progress);
  }

  /**
   * Reset achievements
   */
  reset(): void {
    this.achievements = [];
    this.saveAchievements();
  }

  /**
   * Save achievements to storage
   */
  private saveAchievements(): void {
    this.storage.save("achievements", this.achievements);
  }
}

// Export for global usage
export default AchievementSystem;

// Make available globally for backward compatibility
declare global {
  interface Window {
    AchievementSystem: any;
  }
}

if (typeof window !== "undefined") {
  window.AchievementSystem = AchievementSystem;
}
